import {ListItemActionBar, type ListItemActionBarProps} from '@github-ui/list-view/ListItemActionBar'
import type {CopilotCheckRunFailureContext} from '../../../page-data/payloads/status-checks'
import {publishOpenCopilotChat} from '@github-ui/copilot-chat/utils/copilot-chat-events'
import {CopilotChatIntents} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {parseJobUrlToReference} from '@github-ui/copilot-chat/utils/job-url-parser'
import {ArrowRightIcon, CopilotIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {useCallback} from 'react'
import styles from './StatusCheckRowActionBar.module.css'
import {getFailedActionsJobPrompt} from '@github-ui/copilot-chat/utils/prompts'
import {useAnalytics} from '@github-ui/use-analytics'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {sendEvent} from '@github-ui/hydro-analytics'
import {referenceID} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'

const EXPLAIN_ERROR_ACTION_ID = 'copilot-explain-error-action'
export interface Props {
  copilotCheckRunFailureContext?: CopilotCheckRunFailureContext | null
  targetUrl?: string | null
  displayName?: string | null
}

function CopilotExplainErrorMenuItem({
  failureContext,
  targetUrl,
  displayName,
}: {
  failureContext: CopilotCheckRunFailureContext
  targetUrl?: string | null
  displayName?: string | null
}) {
  const onSelect = useCallback(() => {
    const errorRedirectEnabled = isFeatureEnabled('copilot_chat_action_logs_redirect')
    if (errorRedirectEnabled) {
      sendEvent('copilot.open_copilot_chat', {source: `element ${EXPLAIN_ERROR_ACTION_ID}`})
      const jobReference = targetUrl ? parseJobUrlToReference(targetUrl, true, displayName) : null
      const references = jobReference ? [jobReference] : []
      const prompt = getFailedActionsJobPrompt(undefined, true)
      copilotLocalStorage.setEntrypointMessage({message: prompt, references})
      copilotLocalStorage.setCurrentReferences(null, references)
      window.location.href = jobReference ? `/copilot?reference_id=${referenceID(jobReference)}` : '/copilot'
      return
    } else {
      publishOpenCopilotChat({
        id: EXPLAIN_ERROR_ACTION_ID,
        intent: CopilotChatIntents.actionsAgent,
        content: getFailedActionsJobPrompt(failureContext.jobId),
        references: [],
      })
    }
  }, [failureContext, targetUrl, displayName])

  return (
    <ActionList.Item onSelect={onSelect}>
      Explain error
      <ActionList.LeadingVisual>
        <CopilotIcon />
      </ActionList.LeadingVisual>
    </ActionList.Item>
  )
}

function ViewDetailsMenuItem({targetUrl}: {targetUrl: string}) {
  const {sendAnalyticsEvent} = useAnalytics()

  const handleClick = useCallback(() => {
    sendAnalyticsEvent('status_check_row_action_bar.view_details_click', 'VIEW_DETAILS_MENU_ITEM')
  }, [sendAnalyticsEvent])

  return (
    <ActionList.LinkItem href={targetUrl} onClick={handleClick}>
      View details
      <ActionList.LeadingVisual>
        <ArrowRightIcon />
      </ActionList.LeadingVisual>
    </ActionList.LinkItem>
  )
}

export function StatusCheckRowActionBar({copilotCheckRunFailureContext, targetUrl, displayName}: Props) {
  const staticMenuActions: ListItemActionBarProps['staticMenuActions'] = []

  if (copilotCheckRunFailureContext != null) {
    staticMenuActions.push({
      key: 'copilot-explain-error',
      render: () => (
        <CopilotExplainErrorMenuItem
          failureContext={copilotCheckRunFailureContext}
          targetUrl={targetUrl}
          displayName={displayName}
        />
      ),
    })
  }

  if (targetUrl) {
    staticMenuActions.push({
      key: 'view-details',
      render: () => <ViewDetailsMenuItem targetUrl={targetUrl} />,
    })
  }

  // The label becomes "More actions" when the tooltip appears
  return (
    <ListItemActionBar label="actions" staticMenuActions={staticMenuActions} className={styles.statusCheckActionBar} />
  )
}

try{ CopilotExplainErrorMenuItem.displayName ||= 'CopilotExplainErrorMenuItem' } catch {}
try{ ViewDetailsMenuItem.displayName ||= 'ViewDetailsMenuItem' } catch {}
try{ StatusCheckRowActionBar.displayName ||= 'StatusCheckRowActionBar' } catch {}